// TODO: create currency converter API as different trips might use different currencies
import { parseDateOnly, formatDateRange, calculateDurationFromDates, formatDateOnly } from './dateUtils';

/* Helper: Extract number from price string (e.g. "¥400" -> 400, "$50.50" -> 50.50) */
export const parsePrice = (priceStr) => {

    // Harden parsePrice so bad data never crashes the whole board
    if (typeof priceStr !== 'string') priceStr = priceStr == null ? '' : String(priceStr);

    // If empty/null/undefined, return 0
    if (!priceStr) return 0;

    // If it is already a number -> return it directly
    if (typeof priceStr === 'number') return priceStr;

    // If it is a string (e.g. "¥400"), then clean any non-digit/non-decimal characters
    const cleaned = priceStr.replace(/[^0-9.-]+/g, '');
    return parseFloat(cleaned) || 0;
};

/* Calculate Total Spending for a Trip */
export const calculateTripCost = (trip) => {

    let totalCost = 0;

    if (trip.days) {
        trip.days.forEach(day => {
            const rows = day.rows || [];
            rows.forEach(row => {
                // TODO: handle different key names for price (e.g. cost, amount)
                const amount = row.price
                if (amount) totalCost += parsePrice(amount);
            });
        });
    }

    // Q: What happens if user already set a totalCost field?
    // A: It's okay since this totalCost lives in the root of the trip object, not each trip row.

    // Note: there is also prefix practice "trip._calculatedTotalCost"
    trip.totalCost = totalCost;
    return totalCost;
}

/* Calculate Total Spending & Average All Trips */
export const calculateCostStats = (trips) => {

    let totalSpending = 0;
    let mostExpensiveTrip = { id: '', title: '-', totalCost: 0 };
    let leastExpensiveTrip = { id: '', title: '-', totalCost: Infinity };

    // For each trip
    trips.forEach(trip => {

        const tripCost = calculateTripCost(trip);
        totalSpending += tripCost;

        if (tripCost > mostExpensiveTrip.totalCost) {
            mostExpensiveTrip = { id: trip.id, title: trip.title, totalCost: tripCost, location: trip.locations };
        }
        if (tripCost < leastExpensiveTrip.totalCost) {
            leastExpensiveTrip = { id: trip.id, title: trip.title, totalCost: tripCost, location: trip.locations };
        }
    });

    return {
        totalSpending,
        mostExpensiveTrip,
        leastExpensiveTrip: leastExpensiveTrip.totalCost === Infinity ? { id: '', title: '-', totalCost: 0 } : leastExpensiveTrip
    };
}

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

/* Calculate Travel Stats (Months, Days, Country) */
export const calculateTravelStats = (trips, currentUser) => {

    let totalDays = 0;

    let countriesVisited = {};
    let buddyCounts = {};
    let buddyNames = {};

    let monthCounts = new Array(12).fill(0); // Index 0 = Jan, 11 = Dec
    let currentUserId = currentUser?.id ?? null;

    // Mark today for date clamping
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    trips.forEach(trip => {

        // Count countries visited
        if (trip.locations) {
            const locations = Array.isArray(trip.locations)
                ? trip.locations
                : trip.locations.split(/[·,]/).map(loc => loc.trim());

            locations.forEach(loc => {
                // TODO: standardize country names (e.g. USA, U.S., United States)
                // TODO: handle city vs country names
                if (loc) {
                    countriesVisited[loc] = (countriesVisited[loc] || 0) + 1;
                }
            });
        }

        // Count days travelled
        const start = parseDateOnly(trip.fromDate);
        const end = parseDateOnly(trip.toDate);
        const effectiveEnd = end > today ? today : end;

        for (let d = new Date(start); d <= effectiveEnd; d.setDate(d.getDate() + 1)) {
            totalDays += 1;
            monthCounts[d.getMonth()] += 1;
        }

        // Count buddies
        if (trip.members) {
            trip.members.forEach(member => {
                if (member.user_id !== currentUserId) {
                    buddyCounts[member.user_id] = (buddyCounts[member.user_id] || 0) + 1;
                    buddyNames[member.user_id] = member.display_name;
                }
            });
        }
    });

    // Get top country by frequency
    const topCountry = Object.keys(countriesVisited).length > 0
        ? Object.keys(countriesVisited).reduce((topCountry, currentCountry) =>
            countriesVisited[topCountry] > countriesVisited[currentCountry]
                ? topCountry
                : currentCountry
        )
        : '-';

    const maxMonthCount = Math.max(...monthCounts);
    const topMonth = maxMonthCount > 0 ? monthCounts.indexOf(maxMonthCount) : -1;

    // Get best buddy name
    const bestBuddy = Object.keys(buddyCounts).length > 0
        ? Object.keys(buddyCounts).reduce((bestBuddy, currentBuddy) =>
            buddyCounts[bestBuddy] > buddyCounts[currentBuddy]
                ? bestBuddy
                : currentBuddy
        )
        : '-';
    const bestBuddyName = buddyNames[bestBuddy] || '-';

    return {
        totalTrips: trips.length,
        totalDays,
        totalCountries: Object.keys(countriesVisited).length,
        mostVisitedCountry: topCountry,
        mostActiveMonth: topMonth >= 0 ? monthNames[topMonth] : '-',
        bestBuddy: bestBuddyName
    }
};

/* Detailed per-item breakdowns for the flippable stat-card backs */
export const calculateTravelStatsDetailed = (trips, currentUser) => {

    const currentUserId = currentUser?.id ?? null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate number of trips planned so far (for Trips Planned)
    const tripsPlanned = [...trips]
        .sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate))
        .map(t => ({
            id: t.id,
            title: t.title,
            fromDate: t.fromDate,
            toDate: t.toDate,
            rangeLabel: formatDateRange(t.fromDate, t.toDate),
        }));

    // Calculate duration for each trip (for Days Traveled)
    const perTripDays = [...trips]
        .sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate))
        .map(t => {
            const start = parseDateOnly(t.fromDate);
            const end = parseDateOnly(t.toDate);
            const ongoing = end > today;
            const effectiveEnd = ongoing ? today : end;
            const days = effectiveEnd >= start ? calculateDurationFromDates(start, effectiveEnd) : 0;

            return {
                id: t.id,
                title: t.title,
                rangeLabel: formatDateRange(t.fromDate, t.toDate),
                days,
                ongoing
            }
        })
    const totalDaysClamped = perTripDays.reduce((sum, trip) => sum + trip.days, 0);

    // Calculate countries visited + the trips to each (for Most Visited Country)
    let countryMap = {};
    trips.forEach(trip => {
        if (trip.locations) {
            const locations = Array.isArray(trip.locations)
                ? trip.locations
                : trip.locations.split(/[·,]/).map(loc => loc.trim());

            locations.forEach(loc => {
                if (loc) {
                    if (!countryMap[loc]) {
                        countryMap[loc] = { country: loc, count: 0, trips: [] };
                    }
                    countryMap[loc].count += 1;
                    countryMap[loc].trips.push({ id: trip.id, title: trip.title });
                }
            });
        }
    })
    const countries = Object.values(countryMap)
        .sort((a, b) => b.count - a.count);

    // Calculate the months traveled in (for Most Active Month)
    // monthIndex -> { days, trips: { [tripId]: { id, title, segStartIso, segEndIso } } }
    const monthData = {};
    trips.forEach(t => {
        const start = parseDateOnly(t.fromDate);
        const end = parseDateOnly(t.toDate);
        const effEnd = end > today ? today : end;

        for (let d = new Date(start); d <= effEnd; d.setDate(d.getDate() + 1)) {
            const mi = d.getMonth();
            if (!monthData[mi]) monthData[mi] = { days: 0, trips: {} };
            monthData[mi].days += 1;

            if (!monthData[mi].trips[t.id]) {
                // First day of this trip seen in this month = segment start
                monthData[mi].trips[t.id] = {
                    id: t.id, title: t.title,
                    segStartIso: formatDateOnly(d),
                    segEndIso: formatDateOnly(d),
                };
            }
            // Every day updates the end, so it ends on the last day seen in this month
            monthData[mi].trips[t.id].segEndIso = formatDateOnly(d);
        }
    });
    const months = Object.entries(monthData)
        .map(([mi, data]) => ({
            monthIndex: Number(mi),
            monthName: monthNames[Number(mi)],
            days: data.days,
            trips: Object.values(data.trips).map(seg => ({
                id: seg.id,
                title: seg.title,
                segmentLabel: formatDateRange(seg.segStartIso, seg.segEndIso),
            })),
        }))
        .sort((a, b) => b.days - a.days);
    const maxMonthDays = months.length > 0 ? months[0].days : 0;

    // Calculate buddies (for Best TravelBuddy)
    let buddyMap = {};
    trips.forEach(trip => {
        if (!trip.members) return;
        trip.members.forEach(member => {
            if (member.user_id !== currentUserId) {
                // New buddy
                if (!buddyMap[member.user_id]) {
                    buddyMap[member.user_id] = {
                        user_id: member.user_id,
                        display_name: member.display_name,
                        profile_picture_url: member.profile_picture_url,
                        sharedCount: 0,
                        trips: []
                    }
                }
                buddyMap[member.user_id].sharedCount += 1;
                buddyMap[member.user_id].trips.push({
                    id: trip.id,
                    title: trip.title
                })
            }
        })
    })
    const buddies = Object.values(buddyMap).sort((a, b) => b.sharedCount - a.sharedCount);

    return {
        tripsPlanned,
        perTripDays,
        totalDaysClamped,
        countries,
        months,
        maxMonthDays,
        buddies
    }
};